import { Select, Table, TableColumnsType, TableColumnType } from 'antd';
import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getCurrentSemester } from "../../api/semesters.api";
import { getFeedbackTasks } from "../../api/tutor.api";
import Button from "../../components/Button";
import ErrorResponse from "../../components/ErrorResponse";
import SimpleHeader from "../../components/layout/SimpleHeader";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";
import SelectSemester from "../../components/SelectSemester";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { IGetCurrentSemester } from "../../models/semesters.model";
import { IFeedbackTask, IGetFeedbackTasks } from "../../models/tutor.model";
import SelectEduStep from "../../components/SelectEduStep";
import { getCommonCodeLabel } from "../../lib/store";
import QueryString from "qs";
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';

const DIRECTOR_GROUP = [1000, 1001, 1002, 1003, 1004];

const ClassFeedback = () => {
  const { t, i18n } = useTranslation();
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetFeedbackTasks>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetCurrentSemester>();
  const { auth, currentCampus } = useSelector((root: RootState) => root.auth);
  // const { auth } = useSelector((root: RootState) => root.campus);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IFeedbackTask[]>([]);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [feedbackTypeCode, setFeedbackTypeCode] = useState<string>();
  const [evaluationSttCode, setEvaluationSttCode] = useState<string>();
  const [displayName, setDisplayName] = useState<string>();
  const [studentName, setStudentName] = useState('');
  const [evaluators, setEvaluators] = useState<any>([]);
  const [evaluator, setEvaluator] = useState<any>();

  const isDirectorPermission = (groupNo: number) => DIRECTOR_GROUP.includes(groupNo);

  useEffect(() => {
    execute2(getCurrentSemester());
  }, []);

  useEffect(() => {
    if (res) {
      let newData: IFeedbackTask[] = _.cloneDeep(res.data);

      if (eduStepCode) {
        newData = _.filter(newData, item => item.eduStepCode === eduStepCode);
      }
      if (feedbackTypeCode) {
        newData = _.filter(newData, item => item.feedbackTypeCode === feedbackTypeCode);
      }
      if (evaluationSttCode) {
        newData = _.filter(newData, item => item.evaluationSttCode === evaluationSttCode);
      }
      if (displayName) {
        newData = _.filter(newData, item => item.displayName?.trim() === displayName);
      }
      if (studentName) {
        newData = _.filter(newData, item => item.studentKorName?.includes(studentName) || item.studentEngName?.includes(studentName));
      }

      const teachers = _.map(newData, item => {
        return item.teacherName.split(", ");
      }).flat()
      const teacherNames = [...new Set(teachers)]
      setEvaluators(teacherNames);

      if (evaluator) {
        newData = _.filter(newData, item => item.teacherName.includes(evaluator))
      }

      setData(newData);
    }
  }, [res]);

  useEffect(() => {
    if (res2) {
      onSetSemesterCode(res2.data?.semesterCode);
      doGetFeedbackTasks(res2.data?.semesterCode);
    }
  }, [res2]);

  useEffect(() => {
    window.addEventListener("message", receiveMessage, false);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  const receiveMessage = (event: any) => {
    if (event.data === 'success') {
      const semesterCode = sessionStorage.getItem('semesterCode');
      doGetFeedbackTasks(Number(semesterCode));
    }
  }

  const doGetFeedbackTasks = (_semesterCode?: number) => {
    if (!_semesterCode && !semesterCode) return;

    execute(getFeedbackTasks({
      evaluationSttCode: ['CP', 'SB'],
      semesterCode: _semesterCode || semesterCode!,
      campusCode:  currentCampus
    }));
  };

  const onSearch = () => {
    if (!semesterCode) {
      return infoNotification(t('class.class.message4'));
    }

    setPage(1);
    doGetFeedbackTasks();
  };

  const onSetSemesterCode = (value: number) => {
    setSemesterCode(value);
    sessionStorage.setItem('semesterCode', String(value));
  };

  const getDisplayNameOptions = () => {
    switch (feedbackTypeCode) {
      case 'ST':
        return (
          <>
            <Select.Option value="Hey, Kate!">Hey, Kate!</Select.Option>
            <Select.Option value="Hey, CANBot!">Hey, CANBot!</Select.Option>
            <Select.Option value="Guided Speaking">Guided Speaking</Select.Option>
            <Select.Option value="Creative Speaking">Creative Speaking</Select.Option>

          </>
        );
        case 'WT':
        case 'WS':
          return (
            <>
              <Select.Option value="Paragraph Writing">Paragraph Writing</Select.Option>
              <Select.Option value="Essay Writing">Essay Writing</Select.Option>
            </>
          );
        default:
          return null;
    }
  }

  const columns: TableColumnsType<IFeedbackTask> = [
    {
      title: t('common.label.1'),
      dataIndex: 'eduStepCode',
      key: 'eduStepCode',
      align: 'center',
      render: value => getCommonCodeLabel('PR0006', value, i18n.language),
    },
    {
      title: t('class.eLearnAssess.column1'),
      dataIndex: 'feedbackTypeCode',
      key: 'feedbackTypeCode',
      align: 'center',
      render: value => value === 'ST' ? 'Speaking' : 'Writing',
    },
    {
      title: t('class.eLearnAssess.column2'),
      dataIndex: 'displayName',
      key: 'displayName',
      align: 'center',
      sorter: (a, b) => a.displayName?.localeCompare(b.displayName),
    },
    {
      title: t('class.eLearnAssess.column3'),
      dataIndex: 'curriculumName',
      key: 'curriculumName',
      align: 'center',
      sorter: (a, b) => a.curriculumName?.localeCompare(b.curriculumName),
    },
    {
      title: t('class.eLearnAssess.column4'),
      dataIndex: 'activityName',
      key: 'activityName',
      align: 'center',
      sorter: (a, b) => a.activityName?.localeCompare(b.activityName),
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
      sorter: (a, b) => a.className?.localeCompare(b.className),
    },
    {
      title: t('class.eLearnAssess.column5'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
    },
    {
      title: t('class.eLearnAssess.column8'),
      dataIndex: 'studentEngName',
      key: 'studentEngName',
      align: 'center',
      sorter: (a, b) => a.studentEngName?.localeCompare(b.studentEngName),
    },
    {
      title: t('class.eLearnAssess.column6'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
      render: value => moment(value, 'YYYYMMDD').format('YYYY.MM.DD'),
    },
    {
      title: t('class.eLearnAssess.column7'),
      dataIndex: 'evaluationSttCode',
      key: 'evaluationSttCode',
      sorter: (a, b) => a.evaluationSttCode?.localeCompare(b.evaluationSttCode),
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className={'btn-basic small ' + (value === 'CP' ? 'mint' : 'pink-line')}
            onClick={() => {
              const params: any = {
                rubricNo: record.rubricNo,
                resultCode: record.resultCode,
                learningTlStructureNo: record.learningTlStructureNo,
                seq: record.seq,
                activityCode: record.activityCode,
                curriculumName: record.curriculumName,
                studentKorName: record.studentKorName,
                studentEngName: record.studentEngName,
                regDate: record.regDate?.trim(),
                displayName: record.displayName,
                activityName: record.activityName,
                activityType: record.activityType,
                feedbackTypeCode: record.feedbackTypeCode,
                eduStepCode: record.eduStepCode,
              };
              if (value === 'CP') {
                params.isReadOnly = true;
              }
              if(record.activityType === 'AC') {
                params.classCode = record.classCode;
                params.className = record.className;
                params.evaluatorName = record.evaluatorName;
                params.evaluateDate = record.evaluateDate;
                params.canbotTopic = record.canbotTopic;
                
                window.open(`/assess-cb?${QueryString.stringify(params)}`, '_blank');
              } else {
                window.open(`/assess${record.feedbackTypeCode === 'WS' ? '-ws' : ''}?${QueryString.stringify(params)}`, '_blank');
              }
            }}
            text={value === 'CP' ? t('class.eLearnAssess.button1') : t('class.eLearnAssess.button2')}
          />
        </div>
      )
    }
  ];

  if (isDirectorPermission(auth.groupNo)) {
    const additionalColumn: TableColumnType<IFeedbackTask> = {
      title: t('class.eLearnAssess.column9'),
      dataIndex: 'teacherName',
      key: 'teacherName',
      align: 'center',
      ellipsis: true,
      width: 150,
      sorter: (a, b) => a.teacherName?.localeCompare(b.teacherName),
      render: (value) => {
        return <span className="align-left">{value}</span>
      }
    }
    columns.push(additionalColumn);
  }

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('class.class.placeholder4')}</label>
            <div>
              <SelectSemester
                semesterCode={semesterCode}
                setSemesterCode={onSetSemesterCode}
              />
          </div>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label>
            <div>
              <SelectEduStep
                eduStepCode={eduStepCode}
                setEduStepCode={value => {
                  setEduStepCode(value);
                  setFeedbackTypeCode('');
                }}
                includeAll
              />
            </div>
          </li>
          <li>
            <label>{t('class.eLearnAssess.column1')}</label>
            <div>
              <Select
                placeholder={t('class.eLearnAssess.column1')}
                value={feedbackTypeCode}
                onChange={value => {
                  setFeedbackTypeCode(value);
                  setDisplayName('');
                }}
                disabled={!eduStepCode}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {
                  eduStepCode === 'EM'
                    ? (
                      <>
                        <Select.Option value="ST">Speaking</Select.Option>
                        <Select.Option value="WT">Writing</Select.Option>
                      </>
                      )
                    : (
                      <>
                        <Select.Option value="WT">Writing</Select.Option>
                        <Select.Option value="WS">Writing & Speaking</Select.Option>
                      </>
                      )
                }
              </Select>
            </div>
          </li>
          <li>
            <label>{t('class.eLearnAssess.column2')}</label>
            <div>
              <Select
                placeholder={t('class.eLearnAssess.column2')}
                value={displayName}
                onChange={value => setDisplayName(value)}
                disabled={!feedbackTypeCode}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {getDisplayNameOptions()}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('class.eLearnAssess.column7')}</label>
            <div>
              <Select
                placeholder={t('class.eLearnAssess.column7')}
                value={evaluationSttCode}
                onChange={(value) => setEvaluationSttCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                <Select.Option value="SB">{t('class.eLearnAssess.option1')}</Select.Option>
                <Select.Option value="CP">{t('class.eLearnAssess.button1')}</Select.Option>
              </Select>
            </div>
          </li>

          {
            isDirectorPermission(auth.groupNo) && (
              <li>
                <label>{t('class.eLearnAssess.column9')}</label>
                <div>
                  <Select
                    placeholder={t('class.eLearnAssess.column9')}
                    value={evaluator}
                    onChange={(value) => setEvaluator(value)}
                  >
                    <Select.Option value="">{t('common.text.all')}</Select.Option>
                    {
                      evaluators?.map((name: string) => {
                        return <Select.Option value={name}>{name}</Select.Option>;
                      })
                    }
                  </Select>
                </div>
              </li>
            )
          }
          <li>
            <input
              type="text"
              className="form-control"
              placeholder={t('class.eLearnAssess.placeholder1')}
              value={studentName}
              onChange={e => setStudentName(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {(isLoading || isLoading2) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <SimpleHeader
        parentMenu={t('common.menu.class')}
        currentMenu={t('class.eLearnAssess.title1')}
        onlyTitle={true}
      />

      <div>
      <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
          />
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={data.length}
              page={page}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default ClassFeedback;
